import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ApiEvent } from '@api/model/api-event';
import { convertApiEvent } from '@api/util/api-event.util';
import { AppConfigService } from '@config/app-config.service';
import { environment } from '@environments/environment';
import { Event } from '@model/event';
import { Constants } from '@shared/constants';
import { BaseService } from '@shared/service/base.service';
import { catchError, Observable, of, Subscription, timer } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class EventService extends BaseService {
  private appConfigService = inject(AppConfigService);

  private _event = signal<Event | undefined>(undefined);
  private timerSubscription?: Subscription;

  event = computed<Event | undefined>(() => this._event());

  appVersionCode = computed<string | undefined>(() => {
    const eventCode = this.event()?.code;

    return eventCode
      ? `${environment.production ? '' : `${environment.name}.`}${eventCode}`
      : undefined;
  });

  constructor() {
    super('events');

    effect(() => {
      // # App Init logic
      // - Check the available appConfig signal
      // - If no appConfig found, cancel the possible running refresh timer
      // - If there is an appConfig refresh the active event every x minutes
      // - This x is taken from the appConfig or Constants
      // - Start the new timer to execute getActiveEvent()

      const appConfig = this.appConfigService.appConfig();

      this.timerSubscription?.unsubscribe();

      if (appConfig) {
        const eventSettingsUpdateInterval =
          (appConfig?.eventSettingsUpdateInterval ??
            Constants.EVENT_UPDATE_INTERVAL_MINUTES) * Constants.A_MINUTE;
        this.timerSubscription = // Create a timer to update event settings
          timer(eventSettingsUpdateInterval, eventSettingsUpdateInterval)
            .pipe(mergeMap(() => this.getActiveEvent()))
            .subscribe();
      }
    });
  }

  init(): Observable<void> {
    // Get the active event
    // On fail, the effect will handle the reload timer
    return this.getActiveEvent().pipe(map(() => undefined));
  }

  // Get the active event on resume of the app
  onResume(): Observable<Event | undefined> {
    return this.getActiveEvent();
  }

  private getActiveEvent(): Observable<Event | undefined> {
    // Get the active event from the backend and set it the private _event signal
    // In case of an error the _event will be undefined
    // The code in the effect on _event will handle the retry based on a timer

    return this.httpClient.get<ApiEvent>(`${this.baseUrl}/active`).pipe(
      tap(apiEvent => this._event.set(convertApiEvent(apiEvent))),
      catchError(() => {
        // On error of ApiEvent call the flow needs to continue.
        return of(undefined);
      }),
    );
  }
}
